import { useRef, useState, useEffect } from 'react'
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    PencilSquareIcon,
    BuildingOfficeIcon,
    HashtagIcon,
    ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import { formatDate } from '../../../Common'

const headers = [
    { label: 'Ações',         icon: EyeIcon },
    { label: 'Código',        icon: HashtagIcon },
    { label: 'Empreendimento', icon: BuildingOfficeIcon },
    { label: 'Criação',       icon: CalendarIcon },
    { label: 'Atualização',   icon: CalendarIcon },
]

const InvestimentosTable = ({ data = [], loading, viewInvestimento, editInvestimento, navigateInvestimento, previous, next, page, recordsPerPage }) => {
    const tableScrollRef    = useRef(null)
    const tableContainerRef = useRef(null)
    const [showLeftArrow,  setShowLeftArrow]  = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop,       setArrowTop]       = useState('50%')

    const scrollTable = (direction) => {
        if (tableScrollRef.current) {
            tableScrollRef.current.scrollTo({
                left: tableScrollRef.current.scrollLeft + (direction === 'left' ? -300 : 300),
                behavior: 'smooth',
            })
        }
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            setShowLeftArrow(scrollLeft > 1)
            setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const cRect = tableContainerRef.current.getBoundingClientRect()
            const sRect = tableScrollRef.current.getBoundingClientRect()
            const pct = ((sRect.top - cRect.top + sRect.height / 2) / cRect.height) * 100
            setArrowTop(`${pct}%`)
        }
    }

    useEffect(() => {
        const el = tableScrollRef.current
        if (!el) return
        const timer = setTimeout(() => { checkScrollPosition(); calculateArrowPosition() }, 1)
        const onResize = () => { checkScrollPosition(); calculateArrowPosition() }
        el.addEventListener('scroll', checkScrollPosition)
        window.addEventListener('resize', onResize)
        return () => {
            clearTimeout(timer)
            el.removeEventListener('scroll', checkScrollPosition)
            window.removeEventListener('resize', onResize)
        }
    }, [data, loading])

    const ArrowBtn = ({ dir }) => (
        <button
            onClick={() => scrollTable(dir)}
            className="absolute -translate-y-1/2 z-30 p-2.5 rounded-full bg-gray-900 text-brand-yellow shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center"
            style={{ top: arrowTop, [dir === 'left' ? 'left' : 'right']: '-3.5rem' }}
        >
            {dir === 'left' ? <ChevronLeftIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
        </button>
    )

    return (
        <>
            <div className="relative" ref={tableContainerRef}>
                {showLeftArrow  && <ArrowBtn dir="left" />}
                {showRightArrow && <ArrowBtn dir="right" />}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {/* Título */}
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                        <div className="p-1.5 bg-gray-900 rounded-lg">
                            <BuildingOfficeIcon className="w-4 h-4 text-brand-yellow" />
                        </div>
                        <h2 className="text-sm font-semibold text-gray-900">Lista de Investimentos</h2>
                    </div>

                    <div
                        ref={tableScrollRef}
                        className="overflow-x-auto"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
                        onScroll={checkScrollPosition}
                    >
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <span className="h-8 w-8 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin mb-3" />
                                <p className="text-sm text-gray-400">Carregando investimentos...</p>
                            </div>
                        ) : data.length > 0 ? (
                            <>
                                {/* Header desktop */}
                                <div className="hidden lg:grid grid-cols-5 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    {headers.map((h, i) => (
                                        <div key={i} className="flex items-center justify-center gap-1">
                                            <h.icon className="w-3 h-3" />{h.label}
                                        </div>
                                    ))}
                                </div>

                                <div className="divide-y divide-gray-50">
                                    {data.map((inv) => (
                                        <div key={inv.id} className="px-4 py-3 hover:bg-gray-50 transition-colors duration-150">

                                            {/* Linha desktop */}
                                            <div className="hidden lg:grid grid-cols-5 gap-2 items-center">
                                                {/* Ações */}
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <button
                                                        onClick={() => viewInvestimento?.(inv)}
                                                        className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg hover:bg-brand-yellow/30 transition-colors"
                                                        title="Visualizar"
                                                    >
                                                        <EyeIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => editInvestimento?.(inv)}
                                                        className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                                        title="Editar"
                                                    >
                                                        <PencilSquareIcon className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => navigateInvestimento?.(inv)}
                                                        className="p-1.5 bg-gray-900 text-brand-yellow rounded-lg hover:opacity-80 transition-colors"
                                                        title="Abrir painel"
                                                    >
                                                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                {/* Código */}
                                                <div className="flex justify-center">
                                                    <span className="px-2.5 py-1 bg-gray-900 text-brand-yellow text-xs font-bold rounded-lg">
                                                        #{inv.codigo_empreendimento}
                                                    </span>
                                                </div>

                                                {/* Nome */}
                                                <div className="flex items-center justify-center gap-2">
                                                    <div className="p-1.5 bg-brand-yellow/15 rounded-lg flex-shrink-0">
                                                        <BuildingOfficeIcon className="w-3.5 h-3.5 text-gray-700" />
                                                    </div>
                                                    <span className="text-xs font-medium text-gray-900 truncate" title={inv.nome_empreendimento}>
                                                        {inv.nome_empreendimento || '—'}
                                                    </span>
                                                </div>

                                                {/* Criação */}
                                                <div className="text-center text-xs text-gray-500">
                                                    {formatDate(inv.data_criacao)}
                                                </div>

                                                {/* Atualização */}
                                                <div className="text-center text-xs text-gray-500">
                                                    {formatDate(inv.data_atualizacao)}
                                                </div>
                                            </div>

                                            {/* Card mobile */}
                                            <div className="lg:hidden space-y-3">
                                                {/* Linha 1: código + nome + botões */}
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="px-2 py-1 bg-gray-900 text-brand-yellow text-xs font-bold rounded-lg flex-shrink-0">
                                                            #{inv.codigo_empreendimento}
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{inv.nome_empreendimento || '—'}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                                        <button onClick={() => viewInvestimento?.(inv)} className="p-1.5 bg-brand-yellow/15 text-gray-900 rounded-lg">
                                                            <EyeIcon className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => editInvestimento?.(inv)} className="p-1.5 bg-gray-100 text-gray-600 rounded-lg">
                                                            <PencilSquareIcon className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => navigateInvestimento?.(inv)} className="p-1.5 bg-gray-900 text-brand-yellow rounded-lg">
                                                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Linha 2: datas */}
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Criado em</p>
                                                        <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                                                            <CalendarIcon className="w-3 h-3" />{formatDate(inv.data_criacao)}
                                                        </p>
                                                    </div>
                                                    <div className="bg-gray-50 rounded-lg px-3 py-2">
                                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Atualizado em</p>
                                                        <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                                                            <CalendarIcon className="w-3 h-3" />{formatDate(inv.data_atualizacao)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-3">
                                    <BuildingOfficeIcon className="w-6 h-6 text-brand-yellow" />
                                </div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-1">Nenhum investimento encontrado</h3>
                                <p className="text-xs text-gray-400">Tente ajustar os filtros de busca.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {data.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm mt-4">
                    <span className="text-sm text-gray-500">
                        Página {(page ?? 0) + 1} · {data.length} registros
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={previous}
                            disabled={page === 0}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeftIcon className="w-4 h-4" /> Anterior
                        </button>
                        <button
                            onClick={next}
                            disabled={data.length < (recordsPerPage ?? 10)}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            Próximo <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default InvestimentosTable
